/**
 * Soft-prompt modal that asks the user nicely BEFORE we trigger the iOS push
 * permission dialog. iOS only ever fires its native prompt once — if the user
 * taps "Don't Allow," they can only re-enable from Settings. So we never want
 * to fire the OS dialog cold.
 *
 * This component is pure UI. It does NOT call median.onesignal.register(),
 * does NOT touch Supabase, does NOT persist preferences. The parent owns all
 * of that via the onAccept / onDecline callbacks.
 *
 * Two trigger paths use this same component:
 *   • variant="settings"   — Settings → Notifications toggle flipped ON
 *   • variant="first_add"  — Today tab fired its first successful schedule add
 */

import { useEffect } from "react";
import { trackFeature } from "@/lib/analytics";

interface NotificationSoftPromptProps {
  open: boolean;
  variant: "settings" | "first_add";
  onAccept: () => void;
  onDecline: () => void;
}

const COPY = {
  first_add: {
    headline: "Want a morning heads-up?",
    bodyTop: "Look at you, mama. You just put something on your plate.",
    bodyDescription:
      "Kin can ping you each morning with a quick rundown — anything saved to Today, plus your week ahead.",
    bodyCaveat:
      "Heads up: I won't see live Google Calendar events here yet — only stuff saved or imported into Today. Working on that 💜",
    bodyIosWarning: null,
    bodyBottom: "No spam. Just your actual life. Turn it off anytime.",
    primary: "Yes, set it up 💜",
    secondary: "Maybe later",
  },
  settings: {
    headline: "Quick heads-up before we set this up",
    bodyTop:
      "Kin will ping you each morning with a quick rundown — anything saved to Today, plus your week ahead.",
    bodyDescription: null,
    bodyCaveat:
      "Heads up: I won't see live Google Calendar events here yet — only stuff saved or imported into Today. Working on that 💜",
    bodyIosWarning:
      "iOS only asks once. If you tap Don't Allow on the next screen, you'll need to enable notifications from your phone Settings later.",
    bodyBottom: "Ready?",
    primary: "Yes, ask me 💜",
    secondary: "Not right now",
  },
} as const;

const NotificationSoftPrompt = ({
  open,
  variant,
  onAccept,
  onDecline,
}: NotificationSoftPromptProps) => {
  // Fire-and-forget analytics: prompt-shown when the modal first opens.
  useEffect(() => {
    if (open) trackFeature("notification_prompt_shown", { variant });
  }, [open, variant]);

  if (!open) return null;
  const copy = COPY[variant];

  const handleAccept = () => {
    trackFeature("notification_prompt_accepted", { variant });
    onAccept();
  };
  const handleDecline = () => {
    trackFeature("notification_prompt_dismissed", { variant });
    onDecline();
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0"
      style={{ background: "rgba(28, 26, 62, 0.55)" }}
      onClick={handleDecline}
    >
      <div
        className="w-full max-w-sm bg-white"
        style={{
          borderRadius: 20,
          padding: 24,
          border: "1.5px solid #C8C8F4",
          boxShadow: "0 12px 40px rgba(28, 26, 62, 0.25)",
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="kin-softprompt-headline"
      >
        <div style={{ fontSize: 36, lineHeight: 1, marginBottom: 8 }}>🔔</div>
        <h2
          id="kin-softprompt-headline"
          className="font-display"
          style={{ fontSize: 22, color: "#1C1A3E", lineHeight: 1.2, marginBottom: 12 }}
        >
          {copy.headline}
        </h2>

        <p className="font-body" style={{ fontSize: 14, color: "#54525C", lineHeight: 1.5 }}>
          {copy.bodyTop}
        </p>

        {copy.bodyDescription && (
          <p className="font-body" style={{ fontSize: 14, color: "#54525C", lineHeight: 1.5, marginTop: 10 }}>
            {copy.bodyDescription}
          </p>
        )}

        <p className="font-body" style={{ fontSize: 13, color: "#54525C", lineHeight: 1.5, marginTop: 10, opacity: 0.85 }}>
          {copy.bodyCaveat}
        </p>

        {copy.bodyIosWarning && (
          <p style={{
            fontFamily: "'Instrument Serif', serif",
            fontStyle: "italic",
            fontSize: 16,
            color: "#9090E8",
            lineHeight: 1.5,
            marginTop: 10,
          }}>
            {copy.bodyIosWarning}
          </p>
        )}

        <p className="font-body" style={{ fontSize: 14, color: "#54525C", lineHeight: 1.5, marginTop: 10 }}>
          {copy.bodyBottom}
        </p>

        <button
          onClick={handleAccept}
          className="w-full font-body font-semibold text-white transition-transform active:scale-[0.98]"
          style={{
            background: "#5050C8",
            borderRadius: 14,
            padding: "14px 16px",
            fontSize: 15,
            border: "none",
            marginTop: 20,
            cursor: "pointer",
          }}
        >
          {copy.primary}
        </button>

        <button
          onClick={handleDecline}
          className="w-full font-body"
          style={{
            background: "transparent",
            border: "none",
            color: "#54525C",
            fontSize: 14,
            padding: "12px 16px",
            marginTop: 4,
            cursor: "pointer",
          }}
        >
          {copy.secondary}
        </button>
      </div>
    </div>
  );
};

export default NotificationSoftPrompt;
